<template>
  <ToastHost />
  <ConfirmDialog />
  <!-- Routes publiques (login, mot de passe oublié, reset) : rendu nu. -->
  <router-view v-if="$route.meta?.public" />

  <!-- Coquille applicative : seulement si authentifié. Pendant un logout ou
       une redirection (user vidé mais route non-publique pas encore basculée
       sur /login), aucune branche ne rend → écran neutre le temps d'une frame
       au lieu du dashboard obsolète. Supprime le flash du dashboard au logout. -->
  <div v-else-if="auth.isAuthenticated" class="shell" :class="{ 'shell-compact': isCompact }">
    <!-- Sidebar -->
    <aside class="sidebar" :class="{ 'sidebar-collapsed': sidebarCollapsed }">
      <div class="sb-brand">
        <div class="sb-logo">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <!-- P stylisé -->
            <path d="M7 20V4.8h5.2a4.4 4.4 0 0 1 0 8.8H7" fill="none" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
            <!-- ondes d'appel -->
            <path d="M17.8 7.4a3.3 3.3 0 0 1 0 4.6" fill="none" stroke="white" stroke-width="1.7" stroke-linecap="round" opacity="0.92"/>
            <path d="M20 5.4a6.2 6.2 0 0 1 0 8.4" fill="none" stroke="white" stroke-width="1.7" stroke-linecap="round" opacity="0.58"/>
          </svg>
        </div>
        <span class="sb-name">PexiTalk</span>
        <button
          class="sb-toggle"
          type="button"
          @click="toggleSidebar"
          :title="sidebarCollapsed ? 'Déplier le menu' : 'Replier le menu'"
          :aria-label="sidebarCollapsed ? 'Déplier le menu' : 'Replier le menu'"
        >
          <Icon :name="sidebarCollapsed ? 'chevrons-right' : 'chevrons-left'" :size="16" />
        </button>
      </div>

      <nav class="sb-nav">
        <!-- Groupes de navigation (source unique : useNav, partagée avec la
             recherche globale). Les badges rappels/messages suivent leur page
             quel que soit le groupe où elle se trouve. -->
        <template v-for="(group, gi) in navGroups" :key="group.label ?? 'quotidien'">
          <div v-if="gi > 0" class="sb-divider"></div>
          <div class="sb-group">
            <!-- En-tête de groupe repliable (style CloudTalk). Le badge agrégé
                 signale les rappels/messages en attente d'un groupe replié. -->
            <button
              v-if="group.label"
              type="button"
              class="sb-group-label sb-group-toggle"
              :aria-expanded="!isGroupCollapsed(group.label)"
              :title="isGroupCollapsed(group.label) ? 'Déplier ' + group.label : 'Replier ' + group.label"
              @click="toggleGroup(group.label)"
            >
              <span>{{ group.label }}</span>
              <span v-if="isGroupCollapsed(group.label) && groupBadge(group) > 0" class="sb-badge">{{ groupBadge(group) }}</span>
              <Icon name="chevron-down" :size="13" class="sb-group-chev" :class="{ closed: isGroupCollapsed(group.label) }" />
            </button>
            <!-- Sidebar repliée (icônes seules) : les groupes restent toujours
                 dépliés, sinon plus rien n'est cliquable. -->
            <template v-if="sidebarCollapsed || !group.label || !isGroupCollapsed(group.label)">
              <router-link v-for="item in group.items" :key="item.to" :to="item.to" class="sb-item" active-class="active" :exact="item.exact" :title="sidebarCollapsed ? item.label : ''">
                <Icon :name="item.icon" :size="16" />
                <span>{{ item.label }}</span>
                <span
                  v-if="item.to === '/callbacks' && overdueCallbacks > 0"
                  class="sb-badge"
                  :title="`${overdueCallbacks} rappel(s) en retard`"
                >
                  {{ overdueCallbacks }}
                </span>
                <span
                  v-if="item.to === '/chat' && chat.unreadTotal > 0"
                  class="sb-badge"
                  :title="`${chat.unreadTotal} message(s) non lu(s)`"
                >
                  {{ chat.unreadTotal }}
                </span>
              </router-link>
            </template>
          </div>
        </template>
      </nav>

      <div class="sb-footer">
        <div class="role-tag" :class="auth.user?.role">
          <span class="role-dot" :style="{ background: auth.roleBadge.color }"></span>
          {{ auth.roleBadge.label }}
        </div>
      </div>
    </aside>

    <!-- Main column -->
    <div class="col">
      <header class="topbar" role="banner">
        <!-- Recherche globale : palette ⌘K (pages + centre d'aide). -->
        <GlobalSearch />

        <div class="top-actions">
          <div v-if="inactivityAlert" class="alert-chip">
            <Icon name="alert-triangle" :size="14" />
            <span>Inactivité</span>
          </div>

          <StatusSelector />

          <button
            v-if="!auth.user?.is_staff"
            type="button"
            class="hand-btn"
            :class="{ raised: agent.handRaised }"
            :aria-pressed="agent.handRaised"
            :title="agent.handRaised
              ? 'Vous avez levé la main — cliquez pour la baisser'
              : 'Lever la main pour demander de l\'aide à un superviseur'"
            @click="agent.toggleHand()"
          >
            <Icon name="hand" :size="16" aria-hidden="true" />
            <span class="hand-label">{{ agent.handRaised ? 'Main levée' : 'Aide' }}</span>
          </button>

          <!-- Bascule « mode agent » (superviseurs) : reçoit les appels comme
               un agent tant qu'elle est active ; retour au rôle d'un clic. -->
          <button
            v-if="auth.canUseAgentMode"
            type="button"
            class="agent-mode-btn"
            :class="{ on: auth.actingAsAgent }"
            :aria-pressed="auth.actingAsAgent"
            :disabled="agentModeBusy"
            :title="auth.actingAsAgent
              ? 'Mode agent actif — vous recevez les appels comme un agent. Cliquez pour revenir à votre rôle.'
              : 'Passer en mode agent — recevoir les appels automatiques comme un agent (selon vos assignations).'"
            @click="toggleAgentMode"
          >
            <span v-if="auth.actingAsAgent" class="amb-dot" aria-hidden="true"></span>
            <Icon name="headphones" :size="15" aria-hidden="true" />
            <span class="amb-label">{{ auth.actingAsAgent ? 'Mode agent actif' : 'Mode agent' }}</span>
          </button>

          <!-- Aide contextuelle : ouvre la rubrique d'aide de la page courante. -->
          <button
            type="button"
            class="icon-btn"
            title="Aide sur cette page"
            aria-label="Ouvrir l'aide de cette page"
            @click="openContextHelp"
          >
            <Icon name="help-circle" :size="16" aria-hidden="true" />
          </button>

          <button
            type="button"
            class="icon-btn"
            v-if="auth.user?.is_staff"
            :title="`${adminAlerts} alertes d'inactivité`"
            :aria-label="`Voir la supervision (${adminAlerts} alertes d'inactivité)`"
            @click="router.push('/supervisor')"
          >
            <Icon name="bell" :size="16" aria-hidden="true" />
            <span v-if="adminAlerts > 0" class="badge-dot pulse" aria-hidden="true">{{ adminAlerts }}</span>
          </button>

          <button
            type="button"
            class="user-chip"
            :aria-expanded="userMenu"
            aria-haspopup="menu"
            :aria-label="`Menu utilisateur ${auth.displayName}`"
            @click.stop="userMenu = !userMenu"
            ref="userMenuEl"
          >
            <span class="avatar" aria-hidden="true">
              <img v-if="avatarUrl" :src="avatarUrl" :alt="auth.displayName" class="avatar-img" />
              <template v-else>{{ initial }}</template>
            </span>
            <Icon name="chevron-down" :size="14" />
            <transition name="dd">
              <div v-if="userMenu" class="dropdown" role="menu" @click.stop>
                <div class="dd-header">
                  <div class="dd-name">{{ auth.displayName }}</div>
                  <div class="dd-mail">{{ auth.user?.email || '—' }}</div>
                </div>
                <router-link to="/settings" class="dd-item" role="menuitem" @click="userMenu = false">
                  <Icon name="settings" :size="14" />
                  <span>Paramètres</span>
                </router-link>
                <router-link to="/aide" class="dd-item" role="menuitem" @click="userMenu = false">
                  <Icon name="book-open" :size="14" />
                  <span>Centre d'aide</span>
                </router-link>
                <a v-if="auth.isOwner" :href="apiBase + '/api/docs/'" target="_blank" rel="noopener noreferrer" class="dd-item" role="menuitem">
                  <Icon name="file-text" :size="14" />
                  <span>Documentation API</span>
                </a>
                <hr />
                <button type="button" class="dd-item danger" role="menuitem" @click="doLogout">
                  <Icon name="log-out" :size="14" />
                  <span>Déconnexion</span>
                </button>
              </div>
            </transition>
          </button>
        </div>
      </header>

      <div v-if="inactivityAlert" class="inactivity-banner">
        <Icon name="alert-triangle" :size="14" />
        <span>Inactivité détectée — vous êtes en <b>Disponible</b> depuis plus de 30s sans appel. Pensez à prendre un appel ou changez de statut.</span>
      </div>

      <div v-if="auth.user?.is_staff && adminAlerts > 0" class="admin-banner">
        <Icon name="alert-triangle" :size="14" />
        <span><b>{{ adminAlerts }}</b> agent(s) inactif(s) — voir <router-link to="/supervisor">Supervision</router-link></span>
      </div>

      <a href="#main-content" class="skip-link">Aller au contenu principal</a>
      <div class="body">
        <main id="main-content" class="content" tabindex="-1">
          <router-view v-slot="{ Component }">
            <transition name="page" mode="out-in">
              <component :is="Component" />
            </transition>
          </router-view>
          <!-- Espace de travail d'appel : recouvre la zone de contenu quand
               un appel est en cours (fiche contact en grand, formulaires,
               script, RDV, notes) — le softphone reste visible à droite. -->
          <InCallWorkspace />
        </main>
        <!-- Softphone à droite — en fin d'appel, la fiche de clôture y
             remplace le composeur (le contenu reste accessible).
             `v-show` (et non v-if) : replié, le panneau reste MONTÉ pour ne
             pas couper la connexion SIP ni rater un appel entrant. -->
        <PhonePanel v-show="!phoneCollapsed" @toggle-collapse="togglePhone" />
        <!-- Rail de réouverture quand le téléphone est replié (façon menu). -->
        <button
          v-if="phoneCollapsed"
          class="phone-rail"
          @click="togglePhone"
          title="Afficher le téléphone"
          aria-label="Afficher le panneau téléphone"
        >
          <Icon name="chevrons-left" :size="15" />
          <Icon name="phone" :size="16" />
          <span class="phone-rail-label">Téléphone</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/shared/stores/auth'
import { useAgentStatusStore } from '@/shared/stores/agent_status'
import { useChatStore } from '@/shared/stores/chat'
import { QueueAPI } from '@/shared/api/queue'
import { mediaUrl } from '@/shared/api/client'
import Icon from '@/shared/components/Icon.vue'
import StatusSelector from '@/modules/agent-status/views/StatusSelector.vue'
import PhonePanel from '@/modules/softphone/components/PhonePanel.vue'
import InCallWorkspace from '@/modules/softphone/components/InCallWorkspace.vue'
import ToastHost from '@/shared/components/ToastHost.vue'
import ConfirmDialog from '@/shared/components/ConfirmDialog.vue'
import GlobalSearch from '@/shared/components/GlobalSearch.vue'
import { useNav } from '@/shared/composables/useNav'
import { useScheduleNotifications } from '@/shared/composables/useScheduleNotifications'
import { useCompactMode } from '@/shared/composables/useCompactMode'
import { useToasts } from '@/shared/stores/toasts'
import { useNotifications } from '@/shared/composables/useNotifications'
import { useActiveCallStore } from '@/shared/stores/active_call'

const { isCompact } = useCompactMode()
const auth = useAuthStore()
const agent = useAgentStatusStore()
const chat = useChatStore()
const toasts = useToasts()
const callStore = useActiveCallStore()
const router = useRouter()
const route = useRoute()
const { groups: navGroups } = useNav()
// Rappels de planning (5 min avant chaque bloc) + « planning mis à jour ».
useScheduleNotifications()

// ── Messagerie interne : connectée dès le login pour recevoir les
// notifications (badge sidebar, toasts, notifications natives) même quand
// la vue Messages n'est pas ouverte. ──────────────────────────────────────
watch(
  () => auth.isAuthenticated,
  (logged) => {
    if (logged) chat.init()
    else chat.teardown()
  },
  { immediate: true },
)
const userMenu = ref(false)
const userMenuEl = ref<HTMLButtonElement | null>(null)
const apiBase = import.meta.env.VITE_API_BASE || 'http://localhost:8001'

// ── Bascule « mode agent » (superviseurs) ────────────────────────────────
const agentModeBusy = ref(false)
async function toggleAgentMode() {
  if (agentModeBusy.value) return
  agentModeBusy.value = true
  const target = !auth.actingAsAgent
  try {
    await auth.setAgentMode(target)
    toasts.success(
      target ? 'Mode agent activé' : 'Retour à votre rôle',
      target
        ? 'Vous recevez désormais les appels comme un agent (selon vos assignations).'
        : 'Vous ne recevez plus les appels automatiques.',
    )
  } catch (e: any) {
    toasts.error('Action impossible', e?.response?.data?.detail || 'Réessayez.')
  } finally {
    agentModeBusy.value = false
  }
}

// ── Repli / dépli de la sidebar (persisté) ───────────────────────────────
const SIDEBAR_KEY = 'pexitalk_sidebar_collapsed'
const sidebarCollapsed = ref(localStorage.getItem(SIDEBAR_KEY) === '1')
function toggleSidebar() {
  sidebarCollapsed.value = !sidebarCollapsed.value
  localStorage.setItem(SIDEBAR_KEY, sidebarCollapsed.value ? '1' : '0')
}

// ── Groupes du menu ouvrables/fermables (persisté par libellé) ────────────
const NAV_GROUPS_KEY = 'pexitalk_nav_groups_collapsed'
function readCollapsedGroups(): Set<string> {
  try { return new Set(JSON.parse(localStorage.getItem(NAV_GROUPS_KEY) || '[]')) }
  catch { return new Set() }
}
const collapsedGroups = ref<Set<string>>(readCollapsedGroups())
function isGroupCollapsed(label: string): boolean {
  return collapsedGroups.value.has(label)
}
function toggleGroup(label: string) {
  const next = new Set(collapsedGroups.value)
  if (next.has(label)) next.delete(label)
  else next.add(label)
  collapsedGroups.value = next
  localStorage.setItem(NAV_GROUPS_KEY, JSON.stringify([...next]))
}
/** Badge agrégé d'un groupe (affiché sur l'en-tête quand il est replié). */
function groupBadge(group: { items: Array<{ to: string }> }): number {
  let n = 0
  for (const item of group.items) {
    if (item.to === '/callbacks') n += overdueCallbacks.value
    if (item.to === '/chat') n += chat.unreadTotal
  }
  return n
}

// ── Repli / dépli du panneau téléphone (persisté, façon sidebar) ──────────
const PHONE_KEY = 'pexitalk_phone_collapsed'
const phoneCollapsed = ref(localStorage.getItem(PHONE_KEY) === '1')
function togglePhone() {
  phoneCollapsed.value = !phoneCollapsed.value
  localStorage.setItem(PHONE_KEY, phoneCollapsed.value ? '1' : '0')
}
// Sécurité : on RÉ-OUVRE automatiquement le panneau dès qu'un appel arrive ou
// est en cours (sinon l'agent raterait les commandes d'appel / le décroché).
// On ne touche pas à la préférence persistée : l'agent retrouvera son réglage
// au prochain repli manuel.
watch(
  () => callStore.isActive || callStore.showDisposition || callStore.callStatus === 'ringing',
  (busy) => { if (busy) phoneCollapsed.value = false },
)

const initial = computed(() => auth.displayName.charAt(0).toUpperCase())
const avatarUrl = computed(() => mediaUrl(auth.user?.avatar))

// ── Aide contextuelle : page courante → rubrique (ou article) du Centre
// d'aide. Préfixes de route ; les cibles existent toutes dans content/. ──────
const HELP_ROUTES: Array<[prefix: string, target: string]> = [
  ['/lines', 'appels/lignes'],
  ['/callbacks', 'rappels'],
  ['/planning', 'plannings'],
  ['/appointments', 'suivi/rendez-vous'],
  ['/sales', 'suivi/ventes'],
  ['/repertoire', 'communication/repertoire'],
  ['/chat', 'communication/messagerie'],
  ['/scripts', 'communication/scripts'],
  ['/cdr', 'historique/cdr'],
  ['/recordings', 'historique/favoris'],
  ['/campaigns', 'campagnes'],
  ['/number-lists', 'campagnes/listes'],
  ['/contacts-hub', 'contacts'],
  ['/follow-up', 'suivi/recouvrement-fidelisation'],
  ['/forms', 'campagnes/avance'],
  ['/supervisor', 'supervision/live'],
  ['/agent-time', 'supervision/temps'],
  ['/statistics', 'supervision/stats'],
  ['/performance', 'supervision/performance'],
  ['/qualified-list', 'supervision/liste-qualifiee'],
  ['/users', 'utilisateurs'],
  ['/telephony', 'telephonie'],
  ['/ring-groups', 'telephonie/distribution'],
  ['/blacklist', 'blacklist'],
  ['/call-flows', 'telephonie/automatisation'],
  ['/settings', 'demarrer/parametres'],
]
function openContextHelp() {
  const path = route.path
  let target = ''
  if (path.startsWith('/queue')) {
    // Même page, deux réalités : espace d'appel (agent) vs gestion (admin).
    target = auth.actingAsAgent ? 'appels' : 'campagnes'
  } else {
    const hit = HELP_ROUTES.find(([p]) => path === p || path.startsWith(p + '/'))
    target = hit?.[1] ?? ''
  }
  router.push(target ? `/aide/${target}` : '/aide')
}

const inactivityAlert = computed(() => agent.inactiveAlert && !auth.user?.is_staff)
let inactivityTimer: ReturnType<typeof setInterval> | null = null

function tickInactivity() {
  // Poll backend pour vrai statut d'inactivité
  agent.load().catch(() => {})
}

const adminAlerts = ref(0)
let adminPollTimer: ReturnType<typeof setInterval> | null = null
async function pollAdminAlerts() {
  if (!auth.user?.is_staff) return
  try {
    const agents = await QueueAPI.agents()
    adminAlerts.value = agents.filter((a: any) => a.inactive_alert).length
  } catch {}
}

// ── Rappels : badge sidebar + alertes ────────────────────────────────────
// Agent : ses rappels. Superviseur+ : périmètre « lié à lui » (agents de son
// équipe, campagnes de son équipe, fiches qu'il a traitées — owner/admin :
// tout). Trois signaux, chacun émis UNE fois par rappel (re-signalé si
// l'horaire est reprogrammé) :
//  1. pré-alerte ≤ 5 min avant l'échéance (toast + notification native) ;
//  2. échéance atteinte + agent LIBRE → le numéro est préparé dans le
//     téléphone et l'agent est notifié — l'appel n'est JAMAIS composé seul ;
//  3. superviseur : alerte ROUGE si un rappel échu a son agent indisponible.
const overdueCallbacks = ref(0)
let callbacksPollTimer: ReturnType<typeof setInterval> | null = null
const notifiedCallbacks = new Set<string>()
const preparedCallbacks = new Set<string>()
const alertedUnavailable = new Set<string>()
const CALLBACK_WARN_MS = 5 * 60 * 1000

const AGENT_WORKING_STATUSES = new Set(['available', 'in_call', 'wrap_up'])
const STATUS_LABELS: Record<string, string> = {
  paused: 'en pause', break: 'en pause', away: 'absent',
  training: 'en formation', offline: 'hors ligne',
}

async function pollCallbacks() {
  if (!auth.user) return
  try {
    const sup = auth.isTeamAdmin
    const r = await QueueAPI.myCallbacks(sup ? { related: true } : {})
    overdueCallbacks.value = r.overdue.length
    const now = Date.now()

    // 1) Pré-alerte ≤ 5 min.
    for (const e of [...r.today, ...r.upcoming]) {
      if (!e.callback_at) continue
      const delta = new Date(e.callback_at).getTime() - now
      if (delta <= 0 || delta > CALLBACK_WARN_MS) continue
      const key = `${e.id}@${e.callback_at}`
      if (notifiedCallbacks.has(key)) continue
      notifiedCallbacks.add(key)
      const minutes = Math.max(1, Math.round(delta / 60000))
      const who = e.name || e.phone
      toasts.warning(`Rappel dans ${minutes} min`, `${who} — voir la page Rappels.`)
      if (document.hidden) {
        useNotifications().show('Rappel imminent', `${who} dans ${minutes} min`)
      }
    }

    // 2) Échéance atteinte : si JE suis l'agent assigné, libre et téléphone
    //    inactif → numéro préparé + notification (pas d'appel automatique).
    const mine = r.overdue.filter((e) => e.assigned_to_id === auth.user!.id)
    if (
      mine.length
      && agent.current === 'available'
      && callStore.callStatus === 'idle'
      && !callStore.showDisposition
      && !callStore.dialNumber
    ) {
      const e = mine[0] // le plus ancien (la liste est triée par horaire)
      const key = `${e.id}@${e.callback_at}`
      if (!preparedCallbacks.has(key)) {
        preparedCallbacks.add(key)
        callStore.selectFromQueue(e)
        const who = e.name || e.phone
        toasts.warning(
          'Rappel à passer maintenant',
          `${who} — le numéro est prêt dans le téléphone, appuyez sur Appeler.`,
        )
        useNotifications().show('Rappel à passer', `${who} — numéro prêt.`)
      }
    }

    // 3) Superviseur : rappel échu dont l'agent est indisponible → ROUGE.
    if (sup && r.overdue.length) {
      const agents = await QueueAPI.agents().catch(() => [])
      const statusById: Record<number, string> = {}
      for (const a of agents) statusById[a.user_id] = a.status || 'offline'
      for (const e of r.overdue) {
        if (!e.assigned_to_id || e.assigned_to_id === auth.user.id) continue
        const st = statusById[e.assigned_to_id] || 'offline'
        if (AGENT_WORKING_STATUSES.has(st)) continue
        const key = `${e.id}@${e.callback_at}`
        if (alertedUnavailable.has(key)) continue
        alertedUnavailable.add(key)
        const who = e.name || e.phone
        const stLabel = STATUS_LABELS[st] || st
        toasts.error(
          'Rappel en retard — agent indisponible',
          `${who} : l'agent assigné est ${stLabel}. Réaffectez le rappel ou intervenez (page Rappels).`,
        )
        if (document.hidden) {
          useNotifications().show('Rappel en retard', `${who} — agent ${stLabel}.`)
        }
      }
    }
  } catch {}
}

function doLogout() {
  // Spec : logout → offline immédiat + arrêt des auto-transitions. Ce teardown
  // (best-effort, non bloquant) est CENTRALISÉ dans auth.logout() — même
  // comportement pour ce menu, SettingsView et l'éviction de session — pour ne
  // pas laisser tourner le heartbeat de connexion après invalidation du token.
  auth.logout()
  userMenu.value = false
  router.push('/login')
}

function onDocClick(e: MouseEvent) {
  if (userMenuEl.value && !userMenuEl.value.contains(e.target as Node)) userMenu.value = false
}

function onDocKey(e: KeyboardEvent) {
  if (e.key === 'Escape' && userMenu.value) {
    userMenu.value = false
    userMenuEl.value?.focus()
  }
}

onMounted(() => {
  document.addEventListener('click', onDocClick)
  document.addEventListener('keydown', onDocKey)
  inactivityTimer = setInterval(tickInactivity, 10000)
  if (auth.user?.is_staff) {
    pollAdminAlerts()
    adminPollTimer = setInterval(pollAdminAlerts, 10000)
  }
  pollCallbacks()
  callbacksPollTimer = setInterval(pollCallbacks, 60000)
  // Démarre le timer d'auto-transition de statut (wrap_up→available,
  // available→busy, busy→offline). `load()` l'amorce aussi, mais on le
  // double ici pour couvrir le cas où l'utilisateur est déjà chargé
  // (refresh d'app avec session valide).
  agent.startAutoTransitions()
})
onUnmounted(() => {
  document.removeEventListener('click', onDocClick)
  document.removeEventListener('keydown', onDocKey)
  if (inactivityTimer) clearInterval(inactivityTimer)
  if (adminPollTimer) clearInterval(adminPollTimer)
  if (callbacksPollTimer) clearInterval(callbacksPollTimer)
})
</script>

<style scoped>
.shell {
  display: flex;
  height: 100vh;
  overflow: hidden;
  background: var(--bg);
}

/* ─── Sidebar (dark, Talkdesk-style) ─── */
.sidebar {
  width: 236px;
  min-width: 236px;
  background: linear-gradient(180deg, var(--sidebar-bg-2) 0%, var(--sidebar-bg) 60%);
  display: flex;
  flex-direction: column;
  padding: 18px 12px;
  color: var(--sidebar-text);
  border-right: 1px solid rgba(255,255,255,0.04);
  transition: width var(--duration-base) var(--ease-out),
  min-width var(--duration-base) var(--ease-out),
  padding var(--duration-base) var(--ease-out);
}
.sb-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 8px 20px;
  border-bottom: 1px solid var(--sidebar-border);
  margin-bottom: 14px;
}
.sb-logo {
  width: 32px; height: 32px;
  background: linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%);
  border-radius: 9px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(124,58,237,0.42), inset 0 1px 0 rgba(255,255,255,0.18);
}
.sb-name {
  font-size: 15px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--sidebar-text);
}
/* Bouton replier / déplier */
.sb-toggle {
  margin-left: auto;
  width: 26px; height: 26px;
  display: flex; align-items: center; justify-content: center;
  color: var(--sidebar-text-muted);
  background: rgba(255,255,255,0.04);
  border: 1px solid var(--sidebar-border);
  border-radius: var(--radius-sm);
  cursor: pointer;
  flex-shrink: 0;
}
.sb-toggle:hover { color: var(--sidebar-text); background: var(--sidebar-hover); border-color: rgba(255,255,255,0.14); }

/* ─── État replié (icônes seules) ─── */
.sidebar-collapsed { width: 72px; min-width: 72px; padding: 18px 10px; }
.sidebar-collapsed .sb-name { display: none; }
.sidebar-collapsed .sb-brand {
  flex-direction: column; gap: 12px; padding: 2px 0 18px;
}
.sidebar-collapsed .sb-toggle { margin-left: 0; }
.sidebar-collapsed .sb-group-label { display: none; }
.sidebar-collapsed .sb-item { justify-content: center; padding: 10px; gap: 0; }
.sidebar-collapsed .sb-item > span:not(.sb-badge) { display: none; }
.sidebar-collapsed .sb-badge {
  position: absolute; top: 2px; right: 2px; margin: 0;
  min-width: 15px; height: 15px; padding: 0 3px; line-height: 13px; font-size: 9.5px;
}
.sidebar-collapsed .sb-item.active::before { left: -10px; height: 22px; }
.sidebar-collapsed .sb-footer .role-tag { justify-content: center; padding: 8px 0; font-size: 0; gap: 0; }

.sb-nav { display: flex; flex-direction: column; flex: 1; overflow-y: auto; gap: 2px; }
.sb-group { display: flex; flex-direction: column; gap: 1px; }
.sb-group-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--sidebar-text-muted);
  padding: 6px 11px 4px;
  opacity: 0.55;
}
/* En-tête de groupe repliable (bouton pleine largeur, chevron à droite). */
.sb-group-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  border-radius: var(--radius-sm);
}
.sb-group-toggle:hover { opacity: 1; color: var(--sidebar-text); background: var(--sidebar-hover); }
.sb-group-toggle > span:first-child { flex: 1; }
.sb-group-toggle .sb-badge { margin-left: 0; }
.sb-group-chev { flex-shrink: 0; transition: transform var(--duration-base) var(--ease-out); }
.sb-group-chev.closed { transform: rotate(-90deg); }
.sb-divider {
  height: 1px;
  background: var(--sidebar-border);
  margin: 8px 0;
}
.sb-item {
  position: relative;
  display: flex; align-items: center; gap: 11px;
  padding: 9px 11px;
  border-radius: var(--radius-md);
  color: var(--sidebar-text-muted);
  font-size: 13px;
  font-weight: 500;
}
.sb-item :deep(svg) { transition: transform var(--duration-base) var(--ease-out); }
.sb-item:hover { background: var(--sidebar-hover); color: var(--sidebar-text); }
.sb-item:hover :deep(svg) { transform: scale(1.08); }
.sb-item.active {
  background: linear-gradient(90deg, rgba(124,58,237,0.30), rgba(124,58,237,0.10));
  color: #C4B5FD;
  font-weight: 600;
  box-shadow: inset 0 0 0 1px rgba(124,58,237,0.22);
}
/* Barre d'accent verticale à gauche de l'item actif */
.sb-item.active::before {
  content: '';
  position: absolute; left: -12px; top: 50%; transform: translateY(-50%);
  width: 3px; height: 20px;
  background: #A78BFA;
  border-radius: 0 3px 3px 0;
  box-shadow: 0 0 10px rgba(167,139,250,0.75);
}

.sb-badge {
  margin-left: auto;
  background: var(--danger);
  color: white;
  font-size: 10.5px;
  font-weight: 700;
  min-width: 18px;
  padding: 1px 6px;
  border-radius: var(--radius-full);
  text-align: center;
  line-height: 14px;
}

.sb-footer { padding-top: 12px; border-top: 1px solid var(--sidebar-border); }
.role-tag {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  background: var(--sidebar-hover);
  border-radius: var(--radius);
  font-size: 12px;
  font-weight: 500;
  color: var(--sidebar-text-muted);
}
.role-dot {
  width: 7px; height: 7px;
  border-radius: 50%;
  background: var(--sidebar-text-muted);
}
.role-tag.owner, .role-tag.admin, .role-tag.team_admin { color: var(--sidebar-text); }

/* ─── Main column ─── */
.col { flex: 1; display: flex; flex-direction: column; overflow: hidden; min-width: 0; }

/* ─── Topbar ─── */
.topbar {
  height: 58px;
  background: rgba(255,255,255,0.82);
  backdrop-filter: saturate(160%) blur(12px);
  -webkit-backdrop-filter: saturate(160%) blur(12px);
  border-bottom: 1px solid var(--border);
  box-shadow: 0 1px 0 rgba(16,24,40,0.02), 0 6px 16px -12px rgba(16,24,40,0.18);
  display: flex;
  align-items: center;
  padding: 0 22px;
  gap: 12px;
  flex-shrink: 0;
  z-index: 20;
}

.top-actions { display: flex; align-items: center; gap: 8px; margin-left: auto; }

.alert-chip {
  display: flex; align-items: center; gap: 6px;
  padding: 5px 10px;
  background: var(--warning-bg);
  color: var(--warning);
  border: 1px solid var(--warning-border);
  border-radius: var(--radius-full);
  font-size: 12px;
  font-weight: 500;
}

.inactivity-banner, .admin-banner {
  display: flex; align-items: center; gap: 10px;
  padding: 10px 20px;
  background: var(--warning-bg);
  color: var(--warning);
  border-bottom: 1px solid var(--warning-border);
  font-size: 13px;
  font-weight: 500;
  flex-shrink: 0;
  animation: inact-pulse 2s ease-in-out infinite;
}
.admin-banner {
  background: var(--danger-bg);
  color: var(--danger);
  border-bottom-color: var(--danger-border);
}
.admin-banner a, .inactivity-banner a { color: inherit; text-decoration: underline; font-weight: 600; }
@keyframes inact-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.78; }
}

.badge-dot.pulse { animation: bell-pulse 1.4s infinite; }
@keyframes bell-pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.15); }
}

.icon-btn {
  width: 34px; height: 34px;
  display: flex; align-items: center; justify-content: center;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.1s;
  position: relative;
}
.icon-btn:hover { background: var(--surface-hover); border-color: var(--border-strong); }

/* Bouton « lever la main » (agent → demande d'aide superviseur). */
.hand-btn {
  display: inline-flex; align-items: center; gap: 6px;
  height: 34px; padding: 0 11px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  color: var(--text-secondary);
  font-size: 12.5px; font-weight: 600;
  cursor: pointer;
  transition: all var(--duration-fast);
}
.hand-btn:hover { border-color: var(--warning); color: var(--warning); background: var(--warning-bg); }
.hand-btn.raised {
  background: var(--warning); border-color: var(--warning); color: #fff;
  box-shadow: var(--shadow-sm);
}
.hand-btn.raised :deep(svg) { animation: hand-wave 1.4s ease-in-out infinite; transform-origin: 70% 80%; }
@keyframes hand-wave { 0%, 100% { transform: rotate(0); } 25% { transform: rotate(-12deg); } 75% { transform: rotate(12deg); } }
@media (prefers-reduced-motion: reduce) { .hand-btn.raised :deep(svg) { animation: none; } }
.hand-label { white-space: nowrap; }

/* ── Bascule mode agent (superviseurs) ── */
.agent-mode-btn {
  display: inline-flex; align-items: center; gap: 6px;
  height: 34px; padding: 0 11px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  color: var(--text-secondary);
  font-size: 12.5px; font-weight: 600;
  cursor: pointer;
  transition: all var(--duration-fast);
}
.agent-mode-btn:hover:not(:disabled) { border-color: var(--success); color: var(--success); background: var(--success-bg); }
.agent-mode-btn:disabled { opacity: 0.6; cursor: progress; }
.agent-mode-btn.on {
  background: var(--success); border-color: var(--success); color: #fff;
  box-shadow: var(--shadow-sm);
}
.amb-label { white-space: nowrap; }
.amb-dot { width: 7px; height: 7px; border-radius: 50%; background: #fff; animation: amb-pulse 1.4s ease-in-out infinite; }
@keyframes amb-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
@media (prefers-reduced-motion: reduce) { .amb-dot { animation: none; } }

.badge-dot {
  position: absolute; top: -5px; right: -5px;
  background: var(--danger); color: white;
  font-size: 10px; font-weight: 700;
  min-width: 16px; height: 16px; padding: 0 4px;
  border-radius: 9999px;
  display: flex; align-items: center; justify-content: center;
  border: 2px solid var(--surface);
}

.user-chip {
  display: flex; align-items: center; gap: 6px;
  padding: 4px 8px 4px 4px;
  border-radius: var(--radius);
  cursor: pointer;
  transition: background 0.1s;
  position: relative;
  color: var(--text-secondary);
}
.user-chip:hover { background: var(--surface-hover); }
.avatar {
  width: 30px; height: 30px;
  background: linear-gradient(135deg, #8B5CF6, #6D28D9);
  color: white;
  border-radius: 9px;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px;
  font-weight: 700;
  box-shadow: var(--shadow-primary);
  overflow: hidden;
}
.avatar .avatar-img { width: 100%; height: 100%; object-fit: cover; display: block; }

.dropdown {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow-lg);
  width: 240px;
  z-index: 1000;
  padding: 4px;
  overflow: hidden;
}
.dd-header {
  padding: 10px 12px;
  border-bottom: 1px solid var(--border);
  margin-bottom: 4px;
}
.dd-name { font-size: 13px; font-weight: 600; color: var(--text); }
.dd-mail { font-size: 12px; color: var(--text-muted); margin-top: 2px; }
.dd-item {
  display: flex; align-items: center; gap: 10px;
  width: 100%;
  padding: 7px 10px;
  background: none;
  border: none;
  font-size: 13px;
  color: var(--text-secondary);
  cursor: pointer;
  border-radius: var(--radius-sm);
  text-align: left;
  text-decoration: none;
}
.dd-item:hover { background: var(--surface-hover); color: var(--text); }
.dd-item.danger { color: var(--danger); }
.dd-item.danger:hover { background: var(--danger-bg); }
.dropdown hr { margin: 4px 0; border: none; border-top: 1px solid var(--border); }

/* `min-height: 0` : sans lui, un enfant flex:1 ne rétrécit pas sous la taille
   de son contenu (min-height:auto par défaut) → le contenu déborde 100vh,
   `.shell` (overflow:hidden) le rogne, et le scroll devient inatteignable.
   C'est ce qui rendait la fiche de fin d'appel et les pages longues (Historique…)
   non scrollables. `min-width: 0` : idem sur l'axe horizontal (un tableau large
   ne pousse plus le PhonePanel hors écran ; il scrolle dans `.content`). */
.body { flex: 1; display: flex; overflow: hidden; min-height: 0; }
.content { flex: 1; overflow: auto; min-width: 0; min-height: 0; }

/* Rail de réouverture du téléphone (affiché quand le panneau est replié).
   Bande verticale fine et cliquable, dans l'esprit du menu latéral replié. */
.phone-rail {
  flex-shrink: 0;
  width: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 14px 0;
  background: var(--surface);
  border-left: 1px solid var(--border);
  color: var(--text-muted);
  cursor: pointer;
}
.phone-rail:hover { background: var(--surface-hover); color: var(--primary); }
.phone-rail-label {
  writing-mode: vertical-rl;
  text-orientation: mixed;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  margin-top: 2px;
}

/* Transition de page — fade + léger slide (UX fluide entre écrans) */
.page-enter-active { transition: opacity 0.22s var(--ease-out), transform 0.22s var(--ease-out); }
.page-leave-active { transition: opacity 0.13s var(--ease-out), transform 0.13s var(--ease-out); }
.page-enter-from { opacity: 0; transform: translateY(8px); }
.page-leave-to { opacity: 0; transform: translateY(-4px); }

.dd-enter-active, .dd-leave-active { transition: opacity 0.12s, transform 0.12s; }
.dd-enter-from, .dd-leave-to { opacity: 0; transform: translateY(-4px); }

/* ─── Mode compact (mini softphone) ─────────────────────────────────────
   Quand la fenêtre Electron est rétrécie sous 560 px (manuel ou via le
   bouton "détacher"), on masque sidebar / topbar / contenu pour ne montrer
   que le PhonePanel — qui s'étire alors pour occuper tout l'écran. */
.shell-compact .sidebar,
.shell-compact .topbar,
.shell-compact .content,
.shell-compact .inactivity-banner,
.shell-compact .admin-banner,
.shell-compact .skip-link {
  display: none !important;
}
.shell-compact .col { width: 100%; }
.shell-compact .body { flex: 1; }
.shell-compact :deep(.panel) {
  width: 100% !important;
  min-width: 0 !important;
  border-left: none !important;
  height: 100vh;
}
</style>
